import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Badge, Alert, Button, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { AlertTriangle, TrendingUp, Package, Wallet, Users, Truck, Target, Search, Filter } from "lucide-react";

const LABELS = {
  RECEIVED: "Nuovi",
  WAITING_PAYMENT: "Attesa pagamento",
  PROCESSING: "In lavorazione",
  SHIPPING: "In spedizione",
  IN_DELIVERY: "In consegna",
  DELIVERED: "Consegnati",
};
const COL_TONE = { RECEIVED: "info", WAITING_PAYMENT: "warning",
                   PROCESSING: "info", SHIPPING: "info",
                   IN_DELIVERY: "info", DELIVERED: "success" };

function fmtEur(v) { return `€ ${Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`; }
function fmtKg(v) { return `${Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })} kg`; }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("it-IT") : ""; }
function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoDaysAgo(d) { const x = new Date(); x.setDate(x.getDate() - d); return x.toISOString().slice(0, 10); }

function KpiTile({ icon, label, value, tone = "info", testId }) {
  const bg = { info: "#eff6ff", warning: "#fffbeb", danger: "#fef2f2",
               success: "#f0fdf4", neutral: "#f8fafc" }[tone];
  const fg = { info: "#1d4ed8", warning: "#b45309", danger: "#b91c1c",
               success: "#15803d", neutral: "#334155" }[tone];
  return (
    <Card data-testid={testId} style={{ background: bg }}>
      <Card.Body style={{ padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: fg }}>{icon}</span>
          <span className="muted" style={{ fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</span>
        </div>
        <div style={{ fontSize: "1.4rem", fontWeight: 700, color: fg, marginTop: 4 }}>{value ?? 0}</div>
      </Card.Body>
    </Card>
  );
}

function OrderCard({ order, onConfirmPayment }) {
  const isWaiting = order.status === "WAITING_PAYMENT";
  return (
    <Card data-testid={`sales-card-${order.number}`}
          style={order.has_anomaly ? { borderLeft: "4px solid #dc2626" } : undefined}>
      <Card.Body style={{ padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Link to={`/orders/${order.id}`} style={{ textDecoration: "none", color: "inherit" }}>
              <strong>{order.number}</strong>
            </Link>
            {order.has_anomaly && (
              <Badge tone="danger" style={{ marginLeft: 6 }} data-testid={`anomaly-${order.number}`}>
                <AlertTriangle size={10} /> anomalia
              </Badge>
            )}
          </div>
          <span className="muted" style={{ fontSize: ".72rem" }}>{fmtDate(order.created_at)}</span>
        </div>
        <div style={{ fontSize: ".85rem", marginTop: 4 }}>
          <strong>{order.customer_name}</strong>{" "}
          <span className="muted">{order.customer_code} · {order.customer_region || "—"}</span>
        </div>
        {order.agent_name && (
          <div className="muted" style={{ fontSize: ".72rem" }}>Agente: {order.agent_name}</div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, fontSize: ".85rem" }}>
          <div>
            <strong>{fmtEur(order.total)}</strong> · {fmtKg(order.total_weight_kg)}
          </div>
          {order.pfa_contribution_kg > 0 && (
            <Badge tone="success">PFA {Math.round(order.pfa_contribution_kg)} kg</Badge>
          )}
        </div>
        {order.requested_delivery_date && (
          <div className="muted" style={{ fontSize: ".72rem", marginTop: 2 }}>
            Consegna: {fmtDate(order.requested_delivery_date)}
          </div>
        )}
        <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
          {order.advance_payment && <Badge tone="warning">Ant.</Badge>}
          {order.fulfillment_status === "PARTIAL" && <Badge tone="warning">Parziale</Badge>}
          {isWaiting && (
            <Button size="sm" variant="primary"
                    onClick={(e) => { e.preventDefault(); onConfirmPayment(order.id); }}
                    data-testid={`confirm-pay-${order.number}`}>
              Conferma pagamento
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

export function SalesKanban() {
  const { apiGet, apiPost } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [onlyAnomalies, setOnlyAnomalies] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateField, setDateField] = useState("created");
  const [visibleCols, setVisibleCols] = useState({}); // column -> shown count
  const [limit] = useState(20);

  const buildUrl = () => {
    return `/api/sales/home?limit=${limit}` +
           (q ? `&q=${encodeURIComponent(q)}` : "") +
           (region ? `&region=${encodeURIComponent(region)}` : "") +
           (dateFrom ? `&date_from=${dateFrom}` : "") +
           (dateTo ? `&date_to=${dateTo}` : "") +
           `&date_field=${dateField}` +
           (onlyAnomalies ? "&only_anomalies=true" : "");
  };

  const load = async () => {
    setErr(null);
    try { setData(await apiGet(buildUrl())); }
    catch (e) { setErr(e?.data?.detail || e?.message || String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q, region, onlyAnomalies, dateFrom, dateTo, dateField]);

  const loadMore = async (col) => {
    const bigger = (visibleCols[col] || limit) + 20;
    setVisibleCols((s) => ({ ...s, [col]: bigger }));
    // Refetch column with higher limit — simplistic client-side extension via same endpoint
    const url = `/api/sales/home?limit=${bigger}&column=${col}` +
                (q ? `&q=${encodeURIComponent(q)}` : "") +
                (region ? `&region=${encodeURIComponent(region)}` : "") +
                (dateFrom ? `&date_from=${dateFrom}` : "") +
                (dateTo ? `&date_to=${dateTo}` : "") +
                `&date_field=${dateField}` +
                (onlyAnomalies ? "&only_anomalies=true" : "");
    const d = await apiGet(url);
    setData((old) => ({ ...old, buckets: { ...old.buckets, [col]: d.buckets[col] } }));
  };

  const confirmPayment = async (orderId) => {
    try { await apiPost(`/api/orders/${orderId}/confirm-payment`, { note: "confermato da SalesHome" }); await load(); }
    catch (e) { alert(e?.data?.detail || e?.message || "Errore"); }
  };

  const applyPreset = (p) => {
    if (p === "today") { setDateFrom(isoToday()); setDateTo(isoToday()); }
    else if (p === "7") { setDateFrom(isoDaysAgo(7)); setDateTo(isoToday()); }
    else if (p === "30") { setDateFrom(isoDaysAgo(30)); setDateTo(isoToday()); }
  };

  const regions = useMemo(() => {
    const rs = new Set();
    Object.values(data?.buckets || {}).forEach((b) => b.items.forEach((e) => e.customer_region && rs.add(e.customer_region)));
    return [...rs].sort();
  }, [data]);

  return (
    <>
      <Section title="Dashboard SALES"
               subtitle="Portafoglio ordini · pagamenti · anomalie · PFA"
               data-testid="sales-home">
        {err && <Alert tone="danger">Errore: {err}</Alert>}

        {/* Quick nav */}
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <Link to="/sales/new-order">
            <Button size="sm" variant="primary" data-testid="sales-nav-new-order">
              + Nuovo ordine per cliente
            </Button>
          </Link>
          <Link to="/sales/cooperatives">
            <Button size="sm" data-testid="sales-nav-cooperatives">🏛️ Cooperative</Button>
          </Link>
        </div>

        {/* KPI ROW */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
                      gap: 10, marginBottom: 12 }} data-testid="sales-kpis">
          <KpiTile icon={<TrendingUp size={16} />} label="Nuovi oggi" value={data?.kpis?.orders_new_today} tone="info" testId="kpi-new-today" />
          <KpiTile icon={<Package size={16} />} label="Ordini aperti" value={data?.kpis?.orders_open} tone="info" testId="kpi-open" />
          {data?.kpis?.value_open_eur != null && (
            <KpiTile icon={<Wallet size={16} />} label="Valore aperto" value={fmtEur(data.kpis.value_open_eur)} tone="success" testId="kpi-value" />
          )}
          <KpiTile icon={<Truck size={16} />} label="Kg aperti" value={fmtKg(data?.kpis?.kg_open)} tone="info" testId="kpi-kg" />
          <KpiTile icon={<Wallet size={16} />} label="Da incassare" value={data?.kpis?.orders_waiting_payment} tone="warning" testId="kpi-payment" />
          <KpiTile icon={<Users size={16} />} label="Clienti con anomalia" value={data?.kpis?.customers_with_anomaly} tone="danger" testId="kpi-cust-anom" />
          <KpiTile icon={<Target size={16} />} label="PFA sotto obiettivo" value={data?.kpis?.pfa_behind} tone="warning" testId="kpi-pfa" />
        </div>

        {/* Anomalies summary */}
        {data?.anomalies_grouped?.length > 0 && (
          <Card data-testid="sales-anomalies-summary" style={{ marginBottom: 12 }}>
            <Card.Body style={{ padding: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                            gap: 8, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={16} style={{ color: "#b45309" }} />
                  <strong>Anomalie da gestire</strong>
                </div>
                <Link to="/sales/anomalies">
                  <Button size="sm" data-testid="anomalies-view-all">Vedi anomalie →</Button>
                </Link>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                {data.anomalies_grouped.map((a) => (
                  <div key={a.type} data-testid={`anom-${a.type}`}
                       style={{ padding: "6px 10px", borderRadius: 6,
                                background: "#fef3c7", border: "1px solid #fbbf24" }}>
                    <strong style={{ color: "#b45309" }}>{a.count}</strong>{" "}
                    <span style={{ fontSize: ".85rem" }}>{a.label}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Filters */}
        <Card style={{ marginBottom: 12 }}>
          <Card.Body style={{ padding: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}
                     data-testid="sales-filters">
            <div style={{ flex: "1 1 220px", minWidth: 180 }}>
              <Label>Ricerca</Label>
              <div style={{ position: "relative" }}>
                <Search size={14} style={{ position: "absolute", left: 8, top: 12, color: "#94a3b8" }} />
                <Input data-testid="sales-search"
                       value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Cliente, ordine, codice…"
                       style={{ paddingLeft: 28 }} />
              </div>
            </div>
            <div style={{ minWidth: 160 }}>
              <Label>Regione</Label>
              <select data-testid="sales-region-select"
                      value={region} onChange={(e) => setRegion(e.target.value)}
                      style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}>
                <option value="">Tutte</option>
                {regions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 160 }}>
              <Label>Campo data</Label>
              <select value={dateField} onChange={(e) => setDateField(e.target.value)}
                      data-testid="sales-date-field"
                      style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}>
                <option value="created">Data ordine</option>
                <option value="requested">Data consegna richiesta</option>
              </select>
            </div>
            <div>
              <Label>Da</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                     data-testid="sales-date-from" />
            </div>
            <div>
              <Label>A</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                     data-testid="sales-date-to" />
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <Button size="sm" onClick={() => applyPreset("today")} data-testid="sales-preset-today">Oggi</Button>
              <Button size="sm" onClick={() => applyPreset("7")} data-testid="sales-preset-7">7 giorni</Button>
              <Button size="sm" onClick={() => applyPreset("30")} data-testid="sales-preset-30">30 giorni</Button>
            </div>
            <div>
              <Label>&nbsp;</Label>
              <Button size="sm" variant={onlyAnomalies ? "primary" : "secondary"}
                      onClick={() => setOnlyAnomalies((s) => !s)}
                      data-testid="sales-only-anomalies">
                <Filter size={12} /> Solo anomalie
              </Button>
            </div>
            {(q || region || dateFrom || dateTo || onlyAnomalies) && (
              <Button size="sm"
                      onClick={() => { setQ(""); setRegion(""); setDateFrom(""); setDateTo(""); setOnlyAnomalies(false); }}
                      data-testid="sales-clear-filters">Pulisci</Button>
            )}
          </Card.Body>
        </Card>
      </Section>

      {/* KANBAN */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: 12 }} data-testid="sales-kanban">
        {data?.columns?.map((col) => {
          const b = data.buckets[col] || { items: [], total: 0, has_more: false };
          return (
            <div key={col} data-testid={`kanban-col-${col}`}>
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center", marginBottom: 6, padding: "4px 6px",
                            borderBottom: "3px solid #cbd5e1" }}>
                <strong>{LABELS[col] || col}</strong>
                <Badge tone={COL_TONE[col]}>{b.total}</Badge>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {b.items.length === 0 && <div className="muted" style={{ fontSize: ".78rem", padding: 6 }}>—</div>}
                {b.items.map((o) => (
                  <OrderCard key={o.id} order={o} onConfirmPayment={confirmPayment} />
                ))}
                {b.has_more && (
                  <Button size="sm" onClick={() => loadMore(col)}
                          data-testid={`kanban-more-${col}`}>
                    Mostra altri ({b.total - b.items.length})
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!data && <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>}
    </>
  );
}
